import PropTypes from 'prop-types';
import React, {
  useState, useCallback, useContext, useRef,
} from 'react';

import { ColorProfiles } from '@powdr/constants';
import { AppContext } from '@powdr/context/app-context';
import { getImageAltText } from '@powdr/utils';

import { HotspotModal } from './components';
import {
  StyledHotspotImage, MapScrollContainer, MapCanvas, Image, HotspotsContainer,
  Hotspot, HotspotButton, HotspotIcon,
  HotspotList, HotspotListItem, ItemTitle, ItemIcon, HotspotListContainer,
} from './styles';

export const HotspotImage = ({
  colorProfile = ColorProfiles.BASE,
  isActive,
  isMobileHorizontalScroll = false,
  modalStateChange,
  isShowIconTitles = true,
  relationships,
}) => {
  const { isMobile } = useContext(AppContext);
  const { gatsbyImage: mainImage } = relationships?.mainImage?.relationships?.image || {};
  const { hotspotArray } = relationships?.children?.relationships || [];
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const hotspotsParentRef = useRef(null);

  const handleClose = useCallback((isViaKeypress) => {
    setActiveItemIndex(null);
    modalStateChange(false);

    if (isViaKeypress) {
      hotspotsParentRef.current?.children?.[activeItemIndex]
        ?.querySelector('button')
        ?.focus();
    }
  }, [modalStateChange, hotspotsParentRef, activeItemIndex]);

  const handleOpen = (idx) => {
    setActiveItemIndex(idx);
    modalStateChange(true);
  };

  const showMap = !isMobile || isMobileHorizontalScroll;
  const showList = isMobile && !isMobileHorizontalScroll;

  return (
    <StyledHotspotImage>
      {showMap && (
        <MapScrollContainer
          $isMobileHorizontalScroll={isMobileHorizontalScroll}
          $isModalOpen={activeItemIndex !== null}
        >
          <MapCanvas $isMobileHorizontalScroll={isMobileHorizontalScroll}>
            {(mainImage) && (
              <Image
                image={mainImage}
                alt={getImageAltText(relationships?.mainImage?.relationships?.image)}
              />
            )}
            <HotspotsContainer ref={hotspotsParentRef}>
              {hotspotArray?.map((hotspot, idx) => (
                <Hotspot
                  key={hotspot.id}
                  iconVerticalCoordinate={hotspot.iconVerticalCoordinate}
                  iconHorizontalCoordinate={hotspot.iconHorizontalCoordinate}
                >
                  <HotspotButton
                    type="button"
                    tabIndex={(isActive) ? '-1' : '0'}
                    colorProfile={colorProfile || ColorProfiles.BASE}
                    height={hotspot.iconHeight}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleOpen(idx, 'via hotspot')}
                  >
                    <HotspotIcon
                      name={hotspot.iconName || 'content-pin'}
                      height={hotspot.iconHeight}
                      colorProfile={colorProfile || ColorProfiles.BASE}
                    />
                  </HotspotButton>
                </Hotspot>
              ))}
            </HotspotsContainer>
          </MapCanvas>
        </MapScrollContainer>
      )}
      {showList && (
        <HotspotListContainer>
          <HotspotList>
            {hotspotArray?.map((hotspot, idx) => (
              <HotspotListItem
                key={hotspot.id}
                onClick={() => handleOpen(idx)}
              >
                {(isShowIconTitles) && <ItemTitle>{hotspot.title}</ItemTitle>}
                <ItemIcon
                  name={hotspot.iconName || 'content-pin'}
                  height="45"
                  colorProfile={colorProfile || ColorProfiles.BASE}
                />
              </HotspotListItem>
            ))}
          </HotspotList>
        </HotspotListContainer>
      )}
      {(activeItemIndex !== null) && (
        <HotspotModal
          data={hotspotArray[activeItemIndex]}
          handleClose={handleClose}
          isMobileHorizontalScroll={isMobileHorizontalScroll}
          colorProfile={colorProfile || ColorProfiles.BASE}
        />
      )}
    </StyledHotspotImage>
  );
};

HotspotImage.propTypes = {
  colorProfile: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  isMobileHorizontalScroll: PropTypes.bool,
  modalStateChange: PropTypes.func.isRequired,
  relationships: PropTypes.shape().isRequired,
  isShowIconTitles: PropTypes.bool,
};
